import { cn } from '@shared/utils';

export interface OfflineNoticeCardProps {
  /**
   * 補充說明。省略時用通用文案；各 app 可以換成自己的實情
   * （例如 diet 要講「新增與修改需要連線」，離線可用的 app 則不該這樣寫）。
   */
  description?: string;
  /**
   * 位置覆寫。預設與 `InstallPrompt` 貼在同一個位置（視窗底部）；畫面上另有浮動
   * 按鈕、或可能同時出現安裝提示時要自行讓開，見 {@link OfflineNotice} 的說明。
   */
  className?: string;
}

const DEFAULT_DESCRIPTION = '連線恢復後會自動繼續，不需要重新整理。';

/**
 * 離線提示的外觀。**不含連線偵測**——那在 {@link OfflineNotice} 裡，這一支只負責
 * 長相，Storybook 才有辦法把它叫出來看（瀏覽器不會為了預覽而真的斷網）。
 */
export function OfflineNoticeCard({
  description = DEFAULT_DESCRIPTION,
  className,
}: OfflineNoticeCardProps) {
  return (
    <div
      /*
       * `role="status"`（隱含 aria-live="polite"）：它是自己出現、自己消失的狀態
       * 播報，不是需要立刻打斷手邊操作的警告。用 alert 會在讀屏器上截斷使用者
       * 正在聽的內容——而斷網這件事並不急到那個程度。
       */
      role="status"
      className={cn(
        /* 外觀與 InstallPromptCard 一致：同一種浮動卡片，使用者才會讀成同一類東西 */
        'animate-fade-in fixed inset-x-4 z-[1300]',
        'bottom-[calc(1rem+env(safe-area-inset-bottom))]',
        'rounded-2xl border border-border bg-card-solid p-4 shadow-lg',
        className,
      )}
    >
      <div className="mx-auto flex max-w-5xl items-start gap-3">
        {/*
          圖示直接寫死，不像 InstallPrompt 那樣由呼叫端傳入：那裡的圖示是**這個
          app** 的識別（要讓使用者認得自己在裝什麼），這裡的圖示是**這則訊息**的
          語意，每個 app 都該是同一個。
        */}
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
          <svg
            width={22}
            height={22}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {/* 訊號弧線由外而內，加一道斜槓表示斷開 */}
            <path d="M1 1l22 22" />
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
            <path d="M12 20h.01" />
          </svg>
        </span>

        <div className="flex flex-1 flex-col gap-1">
          <p className="font-display text-sm text-strong">
            目前沒有網路連線
          </p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {description}
          </p>
        </div>

        {/*
          **刻意沒有關閉鍵**，這是與安裝提示最大的不同。安裝提示講的是一個可以拒絕
          的邀請，關掉它等於「我不要裝」；離線講的是當下的事實，關掉不會讓網路回來，
          卻會讓接下來每一次操作失敗都失去解釋。它自己會在連線恢復時消失。
        */}
      </div>
    </div>
  );
}
